import { useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { BankMark } from './BankMark';
import './list.css';

/* ---------- head ---------- */
export function PageHead({ eyebrow, title, sub, right }: {
  eyebrow?: string; title: string; sub?: string; right?: ReactNode;
}) {
  return (
    <div className="list-head">
      <div className="list-head-row">
        <div>
          {eyebrow && <div className="list-eye">{eyebrow}</div>}
          <h1>{title}</h1>
          {sub && <p>{sub}</p>}
        </div>
        {right}
      </div>
    </div>
  );
}

/* ---------- tabs ---------- */
export interface TabItem { key: string; label: string; count?: number }
export function Tabs({ tabs, value, onChange }: { tabs: TabItem[]; value: string; onChange: (k: string) => void }) {
  return (
    <div className="lt-tabs">
      {tabs.map((t) => (
        <button key={t.key} className={`lt-tab${t.key === value ? ' on' : ''}`} onClick={() => onChange(t.key)}>
          {t.label}{t.count != null && <span className="lt-cnt">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------- toolbar ---------- */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="lt-toolbar">{children}</div>;
}
export function Search({ value, onChange, onEnter, placeholder }: {
  value: string; onChange: (v: string) => void; onEnter?: () => void; placeholder?: string;
}) {
  return (
    <div className="lt-search">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4-4" /></svg>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onEnter?.()} />
    </div>
  );
}

/* ---------- table (kompakt + sürükleyerek kolon genişletme) ---------- */
interface HeadCol { label: string; right?: boolean; width?: number }
type HeadItem = string | HeadCol;

export function ListTable({ head, children, empty, pager, storageKey }: {
  head: HeadItem[]; children: ReactNode; empty?: ReactNode; pager?: ReactNode; storageKey?: string;
}) {
  const cols = head.map((h) => (typeof h === 'string' ? { label: h } : h) as HeadCol);
  const defaults = cols.map((c) => c.width ?? 150);
  const [widths, setWidths] = useState<number[]>(() => {
    if (storageKey && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('lt-w:' + storageKey);
        const saved = raw ? JSON.parse(raw) : null;
        if (Array.isArray(saved) && saved.length === defaults.length) return saved.map((n) => Math.max(60, Number(n) || 60));
      } catch { /* yok say */ }
    }
    return defaults;
  });
  const drag = useRef<{ i: number; startX: number; startW: number } | null>(null);

  const move = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const w = Math.max(60, d.startW + (e.clientX - d.startX));
    setWidths((prev) => { const next = prev.slice(); next[d.i] = w; return next; });
  };
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    drag.current = null;
    setWidths((w) => {
      if (storageKey) { try { localStorage.setItem('lt-w:' + storageKey, JSON.stringify(w)); } catch { /* yok say */ } }
      return w;
    });
  };
  const startResize = (i: number) => (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { i, startX: e.clientX, startW: widths[i] };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const totalW = widths.reduce((a, b) => a + b, 0);

  return (
    <div className="lt-card">
      <div className="lt-scroll">
        <table className="lt-table" style={{ minWidth: totalW }}>
          <colgroup>{widths.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <thead>
            <tr>{cols.map((c, i) => (
              <th key={i} className={c.right ? 'r' : undefined}>
                <span className="lt-th-l">{c.label}</span>
                {i < cols.length - 1 && <span className="lt-grip" onPointerDown={startResize(i)} />}
              </th>
            ))}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {empty}
      {pager}
    </div>
  );
}

/* ---------- pager ---------- */
function pageItems(page: number, pages: number): Array<number | '…'> {
  const win: number[] = [];
  for (let p = 1; p <= pages; p++) if (p === 1 || p === pages || Math.abs(p - page) <= 1) win.push(p);
  const out: Array<number | '…'> = [];
  let prev = 0;
  for (const p of win) { if (p - prev > 1) out.push('…'); out.push(p); prev = p; }
  return out;
}
export function Pager({ page, pages, total, unit, onPage }: {
  page: number; pages: number; total?: number; unit?: string; onPage: (p: number) => void;
}) {
  if (pages <= 1 && !total) return null;
  return (
    <div className="lt-pager">
      <div className="info">{total != null ? `${total} ${unit ?? 'kayıt'} · Sayfa ${page}/${pages}` : `Sayfa ${page}/${pages}`}</div>
      <div className="lt-pgs">
        <button className="lt-pg" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
        {pageItems(page, pages).map((it, i) =>
          it === '…' ? <span key={i} className="lt-pg dots">…</span>
            : <button key={i} className={`lt-pg${it === page ? ' on' : ''}`} onClick={() => onPage(it)}>{it}</button>,
        )}
        <button className="lt-pg" disabled={page >= pages} onClick={() => onPage(page + 1)}>›</button>
      </div>
    </div>
  );
}

/* ---------- cells ---------- */
export function BankCell({ bankKey }: { bankKey: string | null }) {
  return <BankMark bankKey={bankKey} />;
}
export function CustomerCell({ primary, secondary }: { primary: string; secondary?: string }) {
  return (
    <div className="lt-cust">
      <div className="ci">{(primary || '?').slice(0, 2).toLocaleUpperCase('tr-TR')}</div>
      <div><b title={primary || undefined}>{primary || '—'}</b>{secondary && <span>{secondary}</span>}</div>
    </div>
  );
}
const PILL: Record<string, { cls: string; label: string }> = {
  approved: { cls: 'ok', label: 'Onaylandı' }, pending: { cls: 'wait', label: 'Bekliyor' }, failed: { cls: 'fail', label: 'Başarısız' },
};
export function StatusPill({ status }: { status: string }) {
  const p = PILL[status] ?? PILL.pending;
  return <span className={`lt-pill ${p.cls}`}>{p.label}</span>;
}

/* ---------- satır aksiyon ikon butonu ---------- */
const ACT_ICONS: Record<string, ReactNode> = {
  view: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  key: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /><path d="M12 15v2" /></>,
  power: <><path d="M12 3v9" /><path d="M6.4 6.4a8 8 0 1 0 11.2 0" /></>,
  trash: <><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" /></>,
  login: <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" /></>,
  download: <><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></>,
};

/** Satır içi ikon buton (tooltip başlıklı). href verilirse bağlantı olarak render eder. */
export function ActBtn({ icon, title, onClick, href, tone = 'default', disabled }: {
  icon: keyof typeof ACT_ICONS; title: string; onClick?: () => void; href?: string;
  tone?: 'default' | 'danger' | 'accent'; disabled?: boolean;
}) {
  const cls = `lt-act${tone !== 'default' ? ' ' + tone : ''}`;
  const svg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {ACT_ICONS[icon]}
    </svg>
  );
  if (href) return <a className={cls} title={title} aria-label={title} href={href} target="_blank" rel="noreferrer">{svg}</a>;
  return <button className={cls} title={title} aria-label={title} onClick={onClick} disabled={disabled}>{svg}</button>;
}

/* ---------- action buttons ---------- */
export function IconBtn({ tone, title, onClick, disabled }: {
  tone: 'ok' | 'no'; title: string; onClick: () => void; disabled?: boolean;
}) {
  return (
    <button className={`lt-ico ${tone}`} title={title} onClick={onClick} disabled={disabled}>
      {tone === 'ok'
        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 13 4 4L19 7" /></svg>
        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6 6 18" /></svg>}
    </button>
  );
}
